/**
 * Pure roster logic for the Students list (`/students`): debt computation,
 * filtering, and sorting. Decoupled from React/Apollo/react-table so the
 * properties below can be property-tested in isolation (design Properties 1–4).
 *
 * The list is paginated; these selectors operate over the rows already loaded
 * into memory. The page shell auto-advances pagination to completion while a
 * filter is active so filtering/counting cover the whole search result
 * (design: roster scalability, Req 3.10 / 4.1).
 */

/**
 * One student-agreement row of the roster, carrying the RAW numeric money
 * values + currency so debt/sort/filter compute on numbers (the display
 * formatting happens in the cell via `formatMoneyCurrency`).
 */
export interface IStudentListRow {
  /** Student global id (row click → card). */
  id: string;
  agreementId?: string;
  /** "Last First Middle". */
  name: string;
  contractTitle: string;
  phone: string;
  /** '' when the agreement has no group. */
  groupName: string;
  currency: string;
  /** Raw — coerced to a number before computing. Terminated rows force debt → 0. */
  budgetAmount: number | string | null;
  /** Raw — coerced to a number before computing. */
  paidAmount: number | string | null;
  terminated: boolean;
}

/** Sortable roster columns. */
export type StudentSortKey = 'name' | 'group' | 'debt' | 'paid';
export type SortDir = 'asc' | 'desc';
export interface IStudentSort {
  key: StudentSortKey;
  dir: SortDir;
}

/** Mutually-exclusive option within the debt-status filter dimension. */
export type DebtFilter = 'any' | 'has' | 'none';
/** Mutually-exclusive option within the agreement-status filter dimension. */
export type StatusFilter = 'any' | 'active' | 'terminated';

/** The four roster filter dimensions, AND-combined across dimensions. */
export interface IRosterFilterState {
  debt: DebtFilter;
  status: StatusFilter;
  group: string | null;
  currency: string | null;
}

/** The neutral filter state (nothing applied). */
export const EMPTY_ROSTER_FILTER: IRosterFilterState = {
  debt: 'any',
  status: 'any',
  group: null,
  currency: null,
};

/** Coerce a possibly string/null money value to a finite number (0 fallback). */
const toNumber = (value: number | string | null | undefined): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Debt for a row: `max(0, budget − paid)`, coercing string-decimal/missing/
 * non-numeric operands to `0`, never negative, and exactly `0` for a terminated
 * agreement (its debt is closed by definition). (Req 1.1, 1.4, 1.7, 15.2.)
 */
export const debtOf = (row: IStudentListRow): number => {
  if (row.terminated) return 0;
  return Math.max(0, toNumber(row.budgetAmount) - toNumber(row.paidAmount));
};

/** Paid amount of a row as a finite number (terminated rows keep their paid). */
const paidOf = (row: IStudentListRow): number => toNumber(row.paidAmount);

/** Whether a row matches the free-text search query (name/contract/phone/group). */
const matchesQuery = (row: IStudentListRow, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [row.name, row.contractTitle, row.phone, row.groupName]
    .filter(Boolean)
    .some((field) => field.toLowerCase().includes(q));
};

/**
 * Filter the roster by every active dimension AND the search query (Req 3).
 *
 * Each dimension is conjunctive with the others and with the search; the debt
 * and status dimensions admit at most one option (the `'any'` sentinel means
 * "no selection in this dimension"). Clearing all dimensions returns exactly the
 * search-matched rows (Req 3.7, 3.8, 3.11).
 */
export const filterRoster = (
  rows: IStudentListRow[],
  filter: IRosterFilterState,
  query: string,
): IStudentListRow[] =>
  rows.filter((row) => {
    if (!matchesQuery(row, query)) return false;

    if (filter.debt === 'has' && debtOf(row) <= 0) return false;
    if (filter.debt === 'none' && debtOf(row) > 0) return false;

    if (filter.status === 'active' && row.terminated) return false;
    if (filter.status === 'terminated' && !row.terminated) return false;

    if (filter.group !== null && row.groupName !== filter.group) return false;
    if (filter.currency !== null && row.currency !== filter.currency) {
      return false;
    }

    return true;
  });

/**
 * Ascending comparator for the numeric/name keys. The `group` key is handled
 * separately in `sortRoster` (empty group names must trail in both directions),
 * so it never reaches this comparator.
 */
const compareBy = (key: StudentSortKey) => (
  a: IStudentListRow,
  b: IStudentListRow,
): number => {
  switch (key) {
    case 'name':
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    case 'debt':
      return debtOf(a) - debtOf(b);
    case 'paid':
      return paidOf(a) - paidOf(b);
    default:
      return 0;
  }
};

/**
 * Sort the (already filtered) rows by `{key, dir}` (Req 2). A stable
 * order-preserving permutation: `desc` is the inverse of `asc` for the same key,
 * except that rows with an empty group name are kept last in both directions for
 * the `group` key (Req 2.4). Returns the input unchanged when `sort` is `null`.
 */
export const sortRoster = (
  rows: IStudentListRow[],
  sort: IStudentSort | null,
): IStudentListRow[] => {
  if (!sort) return rows.slice();

  const base = compareBy(sort.key);
  const factor = sort.dir === 'desc' ? -1 : 1;

  // Partition out empty-group rows for the `group` key so they always trail.
  if (sort.key === 'group') {
    const withGroup = rows.filter((r) => r.groupName);
    const withoutGroup = rows.filter((r) => !r.groupName);
    withGroup.sort((a, b) => {
      const cmp = a.groupName
        .toLowerCase()
        .localeCompare(b.groupName.toLowerCase());
      return cmp * factor;
    });
    return [...withGroup, ...withoutGroup];
  }

  return rows.slice().sort((a, b) => base(a, b) * factor);
};
